import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ApolloLink, InMemoryCache, Observable, from } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { MessageService } from 'primeng/api';
import { environment } from '@/environments/environment';
import { AuthService } from '@/app/core/auth/auth.service';
import { LoadingService } from './loading.service';
import { I18n } from '@/app/core/i18n/i18n';

/** Minimal shape of a GraphQL error as surfaced by Apollo (200 body) or HotChocolate (400). */
type GqlErr = { message: string; extensions?: Record<string, unknown> };

/**
 * Apollo client wired to the Identity GraphQL admin API.
 * Link order: error → loading → auth → http.
 */
export const graphqlProvider = provideApollo(() => {
    const httpLinkFactory = inject(HttpLink);
    const auth = inject(AuthService);
    const loading = inject(LoadingService);
    const messages = inject(MessageService);
    const router = inject(Router);
    const i18n = inject(I18n);

    const http = httpLinkFactory.create({ uri: environment.graphqlEndpoint });

    const authLink = setContext(async () => {
        const token = await auth.getAccessToken();
        return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    });

    const loadingLink = new ApolloLink(
        (operation, forward) =>
            new Observable((observer) => {
                loading.start();
                const sub = forward(operation).subscribe({
                    next: (value) => observer.next(value),
                    error: (err) => {
                        loading.stop();
                        observer.error(err);
                    },
                    complete: () => {
                        loading.stop();
                        observer.complete();
                    }
                });
                return () => sub.unsubscribe();
            })
    );

    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
        // Opt-out for callers that render failures inline (e.g. a drill-down dialog): set
        // `context: { localErrors: true }` on the operation. Session-level recovery (HTTP 401 /
        // AUTH_NOT_AUTHENTICATED → re-login) still applies globally; only the forbidden-page redirect and
        // the error toasts are skipped so the caller can present the failure in place instead of a
        // false-empty result or a full-page bounce.
        const localErrors = operation.getContext()['localErrors'] === true;

        const status = (networkError as { status?: number } | null)?.status;
        if (status === 401) {
            auth.login();
            return;
        }

        // HotChocolate returns HTTP 400 with a GraphQL `errors` array on validation
        // failures (bad scalar, missing required field, etc.). These are NOT in
        // `graphQLErrors` (that's 200-only). Depending on the response content-type,
        // Apollo exposes them on `networkError.result.errors` (parsed) or, when it can't
        // parse, on `networkError.bodyText`. Without this they'd collapse into the generic
        // "Server nije dostupan" toast and read like an outage.
        // apollo-angular uses Angular HttpClient, so a 4xx arrives as an HttpErrorResponse
        // with the parsed body on `.error`; apollo-client's own HttpLink would use `.result`
        // / `.bodyText`. Cover all three so validation errors are never lost.
        const ne = networkError as { error?: unknown; result?: { errors?: GqlErr[] }; bodyText?: string } | null;
        let serverErrors: GqlErr[] | undefined;
        const errBody = ne?.error ?? ne?.result;
        if (errBody && typeof errBody === 'object') {
            serverErrors = (errBody as { errors?: GqlErr[] }).errors;
        }
        for (const raw of [typeof errBody === 'string' ? errBody : undefined, ne?.bodyText]) {
            if (!serverErrors?.length && raw) {
                try {
                    serverErrors = (JSON.parse(raw) as { errors?: GqlErr[] }).errors;
                } catch {
                    /* not JSON — fall through to the generic message */
                }
            }
        }
        const errors: GqlErr[] = graphQLErrors?.length ? graphQLErrors.map((e) => ({ message: e.message, extensions: e.extensions as Record<string, unknown> | undefined })) : (serverErrors ?? []);

        if (errors.length) {
            for (const err of errors) {
                const code = (err.extensions?.['code'] as string) ?? '';
                // Not authenticated (missing/expired/invalid token) → re-login can fix it.
                if (code === 'AUTH_NOT_AUTHENTICATED') {
                    auth.login();
                    return;
                }
                // Authenticated but forbidden → re-login can NOT grant the permission (it would loop, e.g. a
                // freshly-provisioned federated user with no role yet). Send them to the access-denied page —
                // unless the caller opted into local error handling (see `localErrors` above).
                if (code === 'AUTH_NOT_AUTHORIZED') {
                    if (!localErrors) {
                        router.navigateByUrl('/zabranjen-pristup');
                    }
                    return;
                }
                if (!localErrors) {
                    messages.add({ severity: 'error', summary: i18n.t('feedback.error'), detail: err.message });
                }
            }
            return;
        }

        if (networkError && !localErrors) {
            messages.add({ severity: 'error', summary: i18n.t('feedback.connectionSummary'), detail: i18n.t('feedback.connectionDetail') });
        }
    });

    return {
        link: from([errorLink, loadingLink, authLink, http]),
        cache: new InMemoryCache()
    };
});
